import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Copy, CheckCircle, Clock, Upload, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Alert } from '../components/ui/Alert';
import { Badge } from '../components/ui/Badge';
export function PaymentGateway() {
  const navigate = useNavigate();
  const location = useLocation();
  const [timeLeft, setTimeLeft] = useState(3600); // 1 hour in seconds
  const [copied, setCopied] = useState(false);
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const paymentData = location.state || {
    invoiceId: 'INV-2025-001234',
    amount: 500000,
    paymentMethod: 'bank_transfer'
  };
  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor(seconds % 3600 / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleFileSelect = (file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      alert('Ukuran file maksimal 5MB');
      return;
    }
    setProofFile(file);
  };
  const handleUploadProof = () => {
    if (!proofFile) {
      alert('Silakan pilih file bukti pembayaran');
      return;
    }
    setUploading(true);
    setTimeout(() => {
      setUploading(false);
      alert('Bukti pembayaran berhasil diunggah. Kami akan memverifikasi dalam 1x24 jam.');
      navigate('/dashboard');
    }, 2000);
  };
  // Payment instructions based on method
  const paymentInstructions = {
    bank_transfer: {
      title: 'Transfer Bank',
      accountNumber: '1234567890',
      accountName: 'ATIOS Indonesia',
      bankName: 'Bank BCA',
      steps: ['Login ke internet banking atau mobile banking Anda', 'Pilih menu Transfer', 'Masukkan nomor rekening tujuan', 'Masukkan nominal sesuai yang tertera', 'Konfirmasi dan selesaikan transaksi', 'Upload bukti transfer di bawah ini']
    },
    virtual_account: {
      title: 'Virtual Account',
      accountNumber: '8808123456789012',
      accountName: 'ATIOS Indonesia',
      bankName: 'Bank Mandiri',
      steps: ['Login ke ATM, internet banking, atau mobile banking', 'Pilih menu Bayar/Transfer', 'Pilih Virtual Account', 'Masukkan nomor Virtual Account', 'Konfirmasi pembayaran', 'Simpan bukti transaksi']
    }
  };
  const instructions = paymentInstructions[paymentData.paymentMethod as keyof typeof paymentInstructions] || paymentInstructions.bank_transfer;
  return <div className="min-h-screen bg-white" data-id="element-4101">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-4102">
        {/* Header */}
        <div className="mb-8" data-id="element-4103">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4" data-id="element-4104">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-4105" />
            Kembali
          </Button>
          <h1 className="text-3xl font-semibold text-neutral-900 mb-2" data-id="element-4106">
            Instruksi Pembayaran
          </h1>
          <p className="text-neutral-600" data-id="element-4107">
            Selesaikan pembayaran sebelum waktu habis
          </p>
        </div>

        {/* Timer Alert */}
        <Alert variant={timeLeft < 600 ? 'error' : 'warning'} className="mb-8" data-id="element-4108">
          <Clock className="w-5 h-5" data-id="element-4109" />
          <div className="ml-3" data-id="element-4110">
            <p className="font-medium" data-id="element-4111">Waktu Pembayaran Tersisa</p>
            <p className="text-2xl font-bold mt-1" data-id="element-4112">{formatTime(timeLeft)}</p>
          </div>
        </Alert>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-4113">
          {/* Payment Instructions */}
          <div className="lg:col-span-2 space-y-6" data-id="element-4114">
            {/* Account Details */}
            <Card data-id="element-4115">
              <CardHeader data-id="element-4116">
                <CardTitle data-id="element-4117">Detail Rekening</CardTitle>
              </CardHeader>
              <CardContent data-id="element-4118">
                <div className="space-y-4" data-id="element-4119">
                  <div data-id="element-4120">
                    <p className="text-sm text-neutral-600 mb-1" data-id="element-4121">Bank</p>
                    <p className="text-lg font-semibold text-neutral-900" data-id="element-4122">
                      {instructions.bankName}
                    </p>
                  </div>
                  <div data-id="element-4123">
                    <p className="text-sm text-neutral-600 mb-2" data-id="element-4124">
                      Nomor Rekening
                    </p>
                    <div className="flex items-center gap-3 p-4 bg-neutral-50 rounded-lg border border-neutral-200" data-id="element-4125">
                      <p className="text-2xl font-mono font-bold text-neutral-900 flex-1" data-id="element-4126">
                        {instructions.accountNumber}
                      </p>
                      <Button size="sm" variant="secondary" onClick={() => handleCopy(instructions.accountNumber)} data-id="element-4127">
                        {copied ? <>
                            <CheckCircle className="w-4 h-4 mr-1" data-id="element-4128" />
                            Tersalin
                          </> : <>
                            <Copy className="w-4 h-4 mr-1" data-id="element-4129" />
                            Salin
                          </>}
                      </Button>
                    </div>
                  </div>
                  <div data-id="element-4130">
                    <p className="text-sm text-neutral-600 mb-1" data-id="element-4131">Atas Nama</p>
                    <p className="text-lg font-semibold text-neutral-900" data-id="element-4132">
                      {instructions.accountName}
                    </p>
                  </div>
                  <div data-id="element-4133">
                    <p className="text-sm text-neutral-600 mb-2" data-id="element-4134">
                      Jumlah Transfer
                    </p>
                    <div className="flex items-center gap-3 p-4 bg-atios-light rounded-lg border border-atios-primary/20" data-id="element-4135">
                      <p className="text-2xl font-bold text-atios-primary flex-1" data-id="element-4136">
                        Rp {paymentData.amount.toLocaleString('id-ID')}
                      </p>
                      <Button size="sm" variant="secondary" onClick={() => handleCopy(paymentData.amount.toString())} data-id="element-4137">
                        <Copy className="w-4 h-4 mr-1" data-id="element-4138" />
                        Salin
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Steps */}
            <Card data-id="element-4139">
              <CardHeader data-id="element-4140">
                <CardTitle data-id="element-4141">Cara Pembayaran</CardTitle>
              </CardHeader>
              <CardContent data-id="element-4142">
                <ol className="space-y-3" data-id="element-4143">
                  {instructions.steps.map((step, i) => <li key={i} className="flex gap-3" data-id="element-4144">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-atios-primary text-white text-sm font-medium flex items-center justify-center" data-id="element-4145">
                        {i + 1}
                      </span>
                      <span className="text-neutral-700" data-id="element-4146">{step}</span>
                    </li>)}
                </ol>
              </CardContent>
            </Card>

            {/* Upload Proof */}
            <Card data-id="element-4147">
              <CardHeader data-id="element-4148">
                <CardTitle data-id="element-4149">Upload Bukti Pembayaran</CardTitle>
              </CardHeader>
              <CardContent data-id="element-4150">
                <div className="space-y-4" data-id="element-4151">
                  {!proofFile ? <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-neutral-300 rounded-lg cursor-pointer hover:border-atios-primary transition-colors" data-id="element-4152">
                      <Upload className="w-8 h-8 text-neutral-400 mb-2" data-id="element-4153" />
                      <span className="text-sm text-neutral-600" data-id="element-4154">
                        Klik untuk upload bukti transfer
                      </span>
                      <span className="text-xs text-neutral-500 mt-1" data-id="element-4155">
                        JPG, PNG, PDF (Max 5MB)
                      </span>
                      <input type="file" accept="image/*,.pdf" className="hidden" onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) handleFileSelect(file);
                  }} data-id="element-4156" />
                    </label> : <div className="flex items-center justify-between p-4 border border-neutral-200 rounded-lg" data-id="element-4157">
                      <div data-id="element-4158">
                        <p className="font-medium text-neutral-900" data-id="element-4159">
                          {proofFile.name}
                        </p>
                        <p className="text-sm text-neutral-500" data-id="element-4160">
                          {(proofFile.size / 1024).toFixed(0)} KB
                        </p>
                      </div>
                      <Button size="sm" variant="secondary" onClick={() => setProofFile(null)} data-id="element-4161">
                        Hapus
                      </Button>
                    </div>}
                  <Button onClick={handleUploadProof} disabled={!proofFile || uploading} className="w-full" data-id="element-4162">
                    {uploading ? 'Mengunggah...' : 'Kirim Bukti Pembayaran'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Summary */}
          <div className="lg:col-span-1" data-id="element-4163">
            <Card className="sticky top-8" data-id="element-4164">
              <CardHeader data-id="element-4165">
                <CardTitle data-id="element-4166">Ringkasan</CardTitle>
              </CardHeader>
              <CardContent data-id="element-4167">
                <div className="space-y-4" data-id="element-4168">
                  <div data-id="element-4169">
                    <p className="text-sm text-neutral-600 mb-1" data-id="element-4170">Invoice</p>
                    <p className="font-medium text-neutral-900" data-id="element-4171">
                      {paymentData.invoiceId}
                    </p>
                  </div>
                  <div data-id="element-4172">
                    <p className="text-sm text-neutral-600 mb-1" data-id="element-4173">Status</p>
                    <Badge variant="warning" data-id="element-4174">MENUNGGU PEMBAYARAN</Badge>
                  </div>
                  <div className="border-t border-neutral-200 pt-4" data-id="element-4175">
                    <p className="text-sm text-neutral-600 mb-1" data-id="element-4176">Total Bayar</p>
                    <p className="text-2xl font-bold text-atios-primary" data-id="element-4177">
                      Rp {paymentData.amount.toLocaleString('id-ID')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Alert variant="info" className="mt-6" data-id="element-4178">
              <AlertCircle className="w-5 h-5" data-id="element-4179" />
              <div className="ml-3" data-id="element-4180">
                <p className="font-medium text-sm mb-1" data-id="element-4181">Butuh Bantuan?</p>
                <p className="text-xs" data-id="element-4182">
                  Hubungi admin di admin@atios.id atau WhatsApp +62 812 3456
                  7890
                </p>
              </div>
            </Alert>
          </div>
        </div>
      </div>
    </div>;
}